import {
  Button,
  Col,
  type ColumnsType,
  ConfirmModal,
  ContentItem,
  ContentItems,
  Drawer,
  Flex,
  Loading,
  Table,
  TableRowMenuCell,
  Text,
  TitledText,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import {
  useIndicatorPermission,
  useProjectPermission,
} from '@/features/authentication/_hooks/permission';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import {
  useActivities,
  useDeleteIndicator,
  useIndicators,
  useUpdateActivity,
} from '@/features/project/_hooks/activity';
import {
  useProject,
  useUpdateProjectCabinetDecisions,
} from '@/features/project/_hooks/project';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { type ComponentProps, type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { IndicatorTypeLabelModifier } from '../../../_models/activity';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { ActivityFormDrawer } from './ActivityFormDrawer';
import { CabinetDecisionsForm } from './CabinetDecisionsForm';
import { ConsecutiveIndicatorFormDrawer } from './ConsecutiveIndicatorFormDrawer';

export const ActivityDetails: FC = () => {
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();
  const { t } = useTranslation();
  const { openMessage } = useMessage();

  const { data: activitiesData, isLoading: isLoadingUseActivities } =
    useActivities({ projectId });
  const project = useProject(projectId);
  const editProjectPermission = useProjectPermission(true);
  const editIndicatorPermission = useIndicatorPermission(true);
  const hasEditPermission =
    editProjectPermission.data && editIndicatorPermission.data;

  const { data: indicatorsData, isLoading: isLoadingIndicators } =
    useIndicators({
      filters: {
        keyId: projectId,
        ordering: '-type,-term,sort_order',
      },
    });

  const { mutateAsync: updateActivityAsync } = useUpdateActivity();
  const { mutateAsync: updateProjectCabinetDecisionsAsync } =
    useUpdateProjectCabinetDecisions();
  const { mutateAsync: deleteIndicatorAsync } = useDeleteIndicator();

  const [_deleteActivityId, _setDeleteActivityId] = useState<string | null>(
    null,
  );
  const [deleteIndicator, setDeleteIndicator] = useState<{
    id: string;
    json: any;
  } | null>(null);

  const isLoadingActivity = !activitiesData || isLoadingUseActivities;
  const isLoadingIndicator = !indicatorsData || isLoadingIndicators;
  const isProjectLoading = !project.data || project.isLoading;

  if (isLoadingActivity || isLoadingIndicator || isProjectLoading) {
    return <Loading />;
  }

  const fiscalYear = project.data.fiscalYear;

  const isActivityFormDrawerOpen =
    drawerState.open &&
    drawerState.type === 'activity' &&
    drawerState.isEditing;

  const editingActivity = activitiesData.find(
    (activity) =>
      isActivityFormDrawerOpen && drawerState.activityId === activity.id,
  );

  const initialValues: ComponentProps<
    typeof ActivityFormDrawer
  >['initialValues'] = {
    activityContent: editingActivity?.activityContent,
    sortOrder: editingActivity?.sortOrder,
  };

  return (
    <Flex vertical gap={8}>
      <Flex justify="end">
        <PublishedProjectMutableGuard>
          {!project.data.isLocked && (
            <Button
              type="primary"
              onClick={() => {
                setDrawerState({
                  open: true,
                  type: 'activity-indicator-form',
                  isEditing: false,
                });
              }}
            >
              {t('create')}
            </Button>
          )}
        </PublishedProjectMutableGuard>
      </Flex>
      <Table
        rowKey="id"
        dataSource={indicatorsData}
        columns={[
          {
            title: <Text text={t('indicator-type')} />,
            key: 'type',
            dataIndex: 'type',
            render: (type) => (
              <Text weight="regular" text={IndicatorTypeLabelModifier[type]} />
            ),
          },

          {
            title: <Text text={t('indicator-description')} />,
            key: 'description',
            dataIndex: 'description',
            render: (description) => (
              <Text weight="regular" text={description} />
            ),
          },
          {
            title: <Text weight="regular" text={t('unit')} />,
            key: 'unit',
            dataIndex: 'unit',
            render: (unit) => <Text weight="regular" text={unit || null} />,
          },
          {
            title: <Text weight="regular" text={fiscalYear} />,
            key: 'fiscalYear',
            render: (_, record) => (
              <Text weight="regular" text={record.reason || null} />
            ),
          },
          {
            title: <Text weight="regular" text={fiscalYear - 1} />,
            key: 'nextFiscalYear',
            render: (_, record) => (
              <Text weight="regular" text={record.noTypeReason || null} />
            ),
          },
          ...(!project.data.isLocked && hasEditPermission
            ? ([
                {
                  title: '',
                  key: 'actions',
                  width: 150,
                  render: (_, record) => (
                    <PublishedProjectMutableGuard>
                      <TableRowMenuCell
                        onEdit={() => {
                          setDrawerState({
                            open: true,
                            type: 'activity-indicator-form',
                            isEditing: true,
                            indicatorId: record.id,
                          });
                        }}
                        items={[
                          {
                            key: 'delete',
                            label: t('delete'),
                            danger: true,
                            onClick: () => {
                              setDeleteIndicator({
                                id: record.id,
                                json: record.json,
                              });
                            },
                          },
                        ]}
                      />
                    </PublishedProjectMutableGuard>
                  ),
                },
              ] satisfies ColumnsType<(typeof indicatorsData)[number]>)
            : []),
        ]}
      />

      <Flex justify="end">
        {hasEditPermission && !project.data.isLocked && (
          <PublishedProjectMutableGuard>
            <Col flex="none">
              <Button
                type="primary"
                onClick={() => {
                  setDrawerState({
                    open: true,
                    type: 'cabinet-decisions',
                  });
                }}
              >
                {t('edit')}
              </Button>
            </Col>
          </PublishedProjectMutableGuard>
        )}
      </Flex>
      <ContentItem borderBottom={0} paddingBottom={0}>
        <ContentItems
          style={{ marginBottom: 24 }}
          items={[
            <PinpointMarker
              key="name"
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: 'RS',
                rs: {
                  path: { project: projectId },
                  field: 'rs.cabinet-decision-with-defined-indicators-name',
                },
                ksStandard: {
                  path: { project: projectId },
                  field:
                    'ks.standard.cabinet-decision-with-defined-indicators-name',
                },
                ksLocalGovernment: {
                  path: { project: projectId },
                  field:
                    'ks.local-government.cabinet-decision-with-defined-indicators-name',
                },
              })}
            >
              <TitledText
                title={t('analisis-kesesuaian-penggunaan-anggaran')}
                text={project.data.cabinetDecisionWithDefinedIndicators?.name}
              />
            </PinpointMarker>,
            <PinpointMarker
              key="name"
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: 'RS',
                rs: {
                  path: { project: projectId },
                  field:
                    'rs.cabinet-decision-with-defined-indicators-relevant-part',
                },
                ksStandard: {
                  path: { project: projectId },
                  field:
                    'ks.standard.cabinet-decision-with-defined-indicators-relevant-part',
                },
                ksLocalGovernment: {
                  path: { project: projectId },
                  field:
                    'ks.local-government.cabinet-decision-with-defined-indicators-relevant-part',
                },
              })}
            >
              <TitledText
                key="analisis-capaian-tujuan-program"
                title={t('analisis-capaian-tujuan-program')}
                text={
                  project.data.cabinetDecisionWithDefinedIndicators
                    ?.relevantPart
                }
              />
            </PinpointMarker>,
          ]}
        />
      </ContentItem>

      {hasEditPermission && (
        <>
          <ActivityFormDrawer
            open={isActivityFormDrawerOpen}
            initialValues={initialValues}
            onSubmit={async (values) => {
              invariant(
                isActivityFormDrawerOpen && drawerState.activityId,
                t('unreachable-code'),
              );

              try {
                await updateActivityAsync({
                  projectId,
                  id: drawerState.activityId,
                  ...values,
                });

                await Promise.all([
                  openMessage('success', t('message-update-completed')),
                  (async () => {
                    setDrawerState({ open: false });
                  })(),
                ]);
              } catch {
                await openMessage('error', t('message-update-failed'));
              }
            }}
            onClose={() => {
              setDrawerState({ open: false });
            }}
          />

          <Drawer
            open={drawerState.open && drawerState.type === 'cabinet-decisions'}
            onClose={() => {
              setDrawerState({ open: false });
            }}
            destroyOnClose
          >
            <CabinetDecisionsForm
              initialValues={
                project.data.cabinetDecisionWithDefinedIndicators ?? undefined
              }
              onSubmit={async (values) => {
                await updateProjectCabinetDecisionsAsync({
                  id: projectId,
                  ...values,
                });
                await Promise.all([
                  openMessage('success', t('message-update-completed')),
                  (async () => {
                    setDrawerState({ open: false });
                  })(),
                ]);
              }}
            />
          </Drawer>

          <ConsecutiveIndicatorFormDrawer
            fiscalYear={project.data.fiscalYear}
          />

          <ConfirmModal
            skipCheck
            title={t('delete')}
            open={!!deleteIndicator}
            disclaimerText={t('message-delete-indicator')}
            onCancel={() => {
              setDeleteIndicator(null);
            }}
            onOk={async () => {
              invariant(deleteIndicator, t('unreachable-code'));

              try {
                await deleteIndicatorAsync(deleteIndicator.id);
                await Promise.all([
                  openMessage('success', t('message-delete-completed')),
                  (async () => {
                    setDeleteIndicator(null);
                  })(),
                ]);
              } catch {
                await openMessage('error', t('message-delete-failed'));
              }
            }}
          />
        </>
      )}
    </Flex>
  );
};
